import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { Plus, Users, BookOpen } from "lucide-react";
import { listMyClasses, createClass, enrollStudentEmails, assignSimulation, listClassDetails } from "@/lib/api/lxp-university.functions";
import { listPublishedSimulations } from "@/lib/api/lxp-simulations.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/university")({
  head: () => ({ meta: [{ title: "University Dashboard | SA LexPractiq" }] }),
  component: UniversityPage,
});

function UniversityPage() {
  const listFn = useServerFn(listMyClasses);
  const createFn = useServerFn(createClass);
  const enrollFn = useServerFn(enrollStudentEmails);
  const assignFn = useServerFn(assignSimulation);
  const detailsFn = useServerFn(listClassDetails);
  const simsFn = useServerFn(listPublishedSimulations);

  const [classes, setClasses] = useState<any[]>([]);
  const [sims, setSims] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [inst, setInst] = useState("");
  const [selected, setSelected] = useState<any>(null);
  const [emails, setEmails] = useState("");
  const [pickedSim, setPickedSim] = useState<string>("");

  async function reload() {
    const [c, s] = await Promise.all([listFn(), simsFn({ data: { limit: 100 } })]);
    setClasses(c.classes);
    setSims(s.items);
  }
  useEffect(() => { reload(); }, []);

  async function makeClass() {
    if (!name.trim()) return toast.error("Class name required");
    try {
      await createFn({ data: { name, institution: inst || undefined } });
      setName(""); setInst("");
      await reload();
      toast.success("Class created");
    } catch (e: any) { toast.error(e?.message || "Failed. Ensure you have the University role in your dashboard."); }
  }

  async function openDetails(cls: any) {
    const d = await detailsFn({ data: { classId: cls.id } });
    setSelected(d);
  }

  async function enrol() {
    const list = emails.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    await enrollFn({ data: { classId: selected.class.id, emails: list } });
    setEmails("");
    await openDetails(selected.class);
    toast.success("Students enrolled");
  }

  async function doAssign() {
    if (!pickedSim) return;
    await assignFn({ data: { classId: selected.class.id, simulationId: pickedSim } });
    setPickedSim("");
    await openDetails(selected.class);
    toast.success("Simulation assigned");
  }

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <h1 style={{ margin: "0 0 0.5rem" }}>University Dashboard</h1>
        <p className="lxp-muted">Create classes, enrol students, assign simulations.</p>

        <div className="lxp-card" style={{ marginTop: "1.5rem" }}>
          <h3 style={{ margin: "0 0 0.75rem", fontSize: "1rem" }}>New class</h3>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <input className="lxp-input" placeholder="Class name (e.g. LLB Labour 2026)" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
            <input className="lxp-input" placeholder="Institution (optional)" value={inst} onChange={(e) => setInst(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
            <button className="lxp-btn" onClick={makeClass}><Plus size={14} /> Create</button>
          </div>
        </div>

        <div style={{ display: "grid", gap: "0.75rem", marginTop: "1.5rem" }}>
          {classes.map((c) => (
            <button key={c.id} onClick={() => openDetails(c)} className="lxp-card" style={{ textAlign: "left", cursor: "pointer", border: "1px solid var(--lxp-border)" }}>
              <span className="lxp-badge">{c.code}</span>
              <h3 style={{ margin: "0.4rem 0 0.2rem", fontSize: "1rem" }}>{c.name}</h3>
              <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>{c.institution || "—"}</div>
            </button>
          ))}
          {classes.length === 0 && <p className="lxp-muted">No classes yet. Create one above.</p>}
        </div>

        {selected && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "grid", placeItems: "center", padding: "1rem", zIndex: 50 }} onClick={() => setSelected(null)}>
            <div className="lxp-card" style={{ maxWidth: 720, width: "100%", background: "var(--lxp-surface)", maxHeight: "90vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div>
                  <span className="lxp-badge">{selected.class.code}</span>
                  <h2 style={{ margin: "0.4rem 0" }}>{selected.class.name}</h2>
                </div>
                <button className="lxp-btn-ghost" onClick={() => setSelected(null)}>Close</button>
              </div>

              <h4 style={{ margin: "1.5rem 0 0.5rem", fontSize: "0.9rem" }}>
                <Users size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} /> Enrolled students ({selected.enrollments.length})
              </h4>
              <textarea className="lxp-textarea" rows={3} placeholder="Paste student emails (comma or newline separated)…" value={emails} onChange={(e) => setEmails(e.target.value)} />
              <button className="lxp-btn" onClick={enrol} style={{ marginTop: "0.5rem" }}>Enrol students</button>
              <ul style={{ marginTop: "0.75rem", paddingLeft: "1.25rem", fontSize: "0.85rem" }} className="lxp-muted">
                {selected.enrollments.map((e: any) => <li key={e.id}>{e.student_email}</li>)}
              </ul>

              <h4 style={{ margin: "1.5rem 0 0.5rem", fontSize: "0.9rem" }}>
                <BookOpen size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} /> Assigned simulations ({selected.assignments.length})
              </h4>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <select className="lxp-select" value={pickedSim} onChange={(e) => setPickedSim(e.target.value)}>
                  <option value="">— Pick a simulation —</option>
                  {sims.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
                <button className="lxp-btn" onClick={doAssign} disabled={!pickedSim}>Assign</button>
              </div>
              <ul style={{ marginTop: "0.75rem", paddingLeft: "1.25rem", fontSize: "0.85rem" }} className="lxp-muted">
                {selected.assignments.map((a: any) => <li key={a.id}>{a.simulation?.title}{a.due_at ? ` — due ${new Date(a.due_at).toLocaleDateString()}` : ""}</li>)}
              </ul>
            </div>
          </div>
        )}
      </div>
    </LxpLayout>
  );
}
